import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DirectorClaim() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invite token"); return; }
    api.get(`/director/invite/${token}`).then((r) => setInvite(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Invite not found"));
  }, [token]);

  const claim = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/director/claim", { token, password });
      setUser(data);
      toast.success("Welcome! Your director portal is ready.");
      navigate("/director");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not claim invite"); }
    finally { setLoading(false); }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center px-4 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,92,0.12),transparent_60%)]" />
      <div className="relative w-full max-w-md glass-strong rounded-2xl p-8" data-testid="director-claim">
        <div className="mb-6 flex items-center justify-center gap-2"><Crown className="h-7 w-7 text-gold" /><span className="font-serif text-lg text-amber-50">National Director Invitation</span></div>
        {error ? (
          <p className="rounded-lg bg-destructive/15 px-4 py-3 text-center text-sm text-red-300" data-testid="claim-error">{error}</p>
        ) : !invite ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : (
          <>
            <h1 className="text-center text-2xl text-amber-50">Welcome, {invite.name}</h1>
            <p className="mb-6 mt-1 text-center text-sm text-amber-100/60">You've been invited to lead <span className="gold-text">{invite.delegation_name}</span>. Set a password to claim your portal.</p>
            <form onSubmit={claim} className="space-y-4">
              <input required type="password" minLength={6} placeholder="Create a password (min 6)" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-password" className="w-full rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold" />
              <button disabled={loading} data-testid="claim-submit" className="btn-gold flex w-full items-center justify-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Claim My Portal</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
